import * as React from "react";
import { cn } from "../_internal/cn";
import type { TypeNarrativeHeadlineProps } from "./NarrativeHeadlineTypes";

/**
 * NarrativeHeadline — the titling primitive for a narrative block. A single
 * heading element, nothing more: supporting copy and tags/tokens belong to
 * sibling primitives that compose alongside it, not baked into the title.
 *
 * Wrap part of the headline in `<NarrativeHeadlineHighlight>` for a static
 * gradient accent, or `<NarrativeHeadlineRoll>` for the rolling "roll" variant.
 *
 * Styled via `narrative-headline.css` (Seeds CSS custom properties). Consumers
 * import the classes through `@sproutsocial/racine/css/components`. Overrides
 * are done with standard `className` / `style`.
 */
const NarrativeHeadline = React.forwardRef<
  HTMLHeadingElement,
  TypeNarrativeHeadlineProps
>(
  (
    { children, headingLevel = 2, size = "default", className, ...props },
    ref
  ) => {
    const Heading = `h${headingLevel}` as const;

    return (
      <Heading
        ref={ref}
        className={cn(
          "seeds-narrative-headline",
          { "seeds-narrative-headline-small": size === "small" },
          className
        )}
        {...props}
      >
        {children}
      </Heading>
    );
  }
);

NarrativeHeadline.displayName = "NarrativeHeadline";

export default NarrativeHeadline;
